import React, { useState } from 'react';
import { Outlet } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Business,
  CalendarToday,
  People,
  AccountCircle,
  Logout,
  Dashboard,
  AttachMoney,
  AccountBalance,
  Settings,
  Storage,
  ChevronLeft,
  ChevronRight,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher';
import { useLanguage } from '../contexts/LanguageContext';
import { useSections } from '../contexts/SectionsContext';
import { useSidebar } from '../contexts/SidebarContext';

interface NavItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  adminOnly?: boolean;
}

export const Layout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isAdmin = user?.role === 'admin';

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  // Get sidebar state from context
  const { sidebarCollapsed, setSidebarCollapsed, drawerWidth, collapsedDrawerWidth } = useSidebar();
  
  // Get sections state from context
  const { showSectionsAboveCalendar, setShowSectionsAboveCalendar } = useSections();

  // Define nav items with translations
  const navItems: NavItem[] = [
    { text: t('dashboard'), icon: <Dashboard />, path: '/dashboard' },
    { text: t('properties'), icon: <Business />, path: '/properties' },
    { text: t('bookings'), icon: <CalendarToday />, path: '/bookings' },
    { text: t('accounting'), icon: <AccountBalance />, path: '/accounting' },
    { text: t('payment_vouchers'), icon: <AttachMoney />, path: '/payment-vouchers' },
    { text: t('currencies'), icon: <AttachMoney />, path: '/currencies', adminOnly: true },
    { text: t('users'), icon: <People />, path: '/users', adminOnly: true },
    { text: t('database_admin'), icon: <Storage />, path: '/database-admin', adminOnly: true },
    { text: 'Settings', icon: <Settings />, path: '/settings', adminOnly: true },
    { text: t('profile'), icon: <AccountCircle />, path: '/profile' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login');
  };

  const handleProfileClick = () => {
    handleMenuClose();
    navigate('/profile');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const drawer = (
    <Box>
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" noWrap component="div" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
          {t('property_management')}
        </Typography>
        <IconButton
          onClick={handleSidebarToggle}
          sx={{ 
            display: { xs: 'none', md: 'block' },
            ...(isRTL ? { ml: 1 } : { mr: 1 })
          }}
        >
          <ChevronLeft />
        </IconButton>
      </Toolbar>
      <Divider />
      <List>
        {navItems
          .filter(item => !item.adminOnly || isAdmin)
          .map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigation(item.path)}
                sx={{
                  mx: 1,
                  borderRadius: 2,
                  minHeight: 48,
                  justifyContent: 'flex-start',
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: location.pathname === item.path ? 'white' : 'text.secondary',
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ 
      display: 'flex',
      flexDirection: isRTL ? 'row-reverse' : 'row'
    }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { md: sidebarCollapsed ? '100%' : `calc(100% - ${drawerWidth}px)` },
          ...(isRTL
            ? { mr: { md: sidebarCollapsed ? 0 : `${drawerWidth}px` } }
            : { ml: { md: sidebarCollapsed ? 0 : `${drawerWidth}px` } }
          ),
          backgroundColor: 'white',
          color: 'text.primary',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={isMobile ? handleDrawerToggle : handleSidebarToggle}
            sx={{ 
              ...(isRTL ? { ml: 2 } : { mr: 2 }), 
              display: { xs: 'none', md: sidebarCollapsed ? 'block' : 'none' } 
            }}
          >
            <MenuIcon />
          </IconButton>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              ...(isRTL ? { ml: 2 } : { mr: 2 }), 
              // Only show this mobile menu button on xs-sm when the sidebar toggle button is hidden
              display: { xs: 'block', md: 'none' } 
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {navItems.find(item => item.path === location.pathname)?.text || 'Dashboard'}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Sections Toggle Button - Only show on bookings page */}
            {location.pathname === '/bookings' && (
              <IconButton
                size="small"
                onClick={() => setShowSectionsAboveCalendar(!showSectionsAboveCalendar)}
                sx={{ 
                  border: 1,
                  borderColor: 'grey.400', 
                  color: 'grey.600',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: 'rgba(25, 118, 210, 0.1)',
                  }
                }}
              >
                {showSectionsAboveCalendar ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            )}
            <LanguageSwitcher />
            <Typography variant="body2" sx={{ color: 'text.secondary', display: { xs: 'none', sm: 'block' } }}>
              {user?.name}
              {isAdmin && (
                <Box
                  component="span"
                  sx={{
                    ml: 1,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    backgroundColor: 'secondary.main',
                    color: 'white',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                  }}
                >
                  {t('admin')}
                </Box>
              )}
            </Typography>
            <IconButton
              size="small"
              onClick={handleMenuClick}
              sx={{ ...(isRTL ? { mr: 2 } : { ml: 2 }) }}
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                {user?.name?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem onClick={handleProfileClick}>
                <ListItemIcon>
                  <AccountCircle fontSize="small" />
                </ListItemIcon>
                {t('profile')}
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                {t('logout')}
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ 
          width: { md: sidebarCollapsed ? 0 : drawerWidth }, 
          flexShrink: { md: 0 },
          ...(isRTL ? { order: 2 } : {}),
          display: { md: sidebarCollapsed ? 'none' : 'block' }
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              ...(isRTL ? { right: 0, left: 'auto' } : {})
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
              ...(isRTL ? { right: 0, left: 'auto' } : {})
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: sidebarCollapsed ? '100%' : `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: 'background.default',
          ...(isRTL ? { order: 1 } : {})
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};
